import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Info, Copy, Check, AlertCircle, ClipboardCheck, Type, Hash, Calendar, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

const MAX_NAME_LENGTH = 40;

interface InformationTabProps {
    name: string;
    setName: (name: string) => void;
    nameError: string;
    setNameError: (error: string) => void;
    assistantId?: string;
    createdAt?: string;
    updatedAt?: string;
}

export function InformationTab({
    name,
    setName,
    nameError,
    setNameError,
    assistantId,
    createdAt,
    updatedAt,
}: InformationTabProps) {
    const { success, error } = useEnhancedToast();
    const [localName, setLocalName] = useState(name);
    const [showCopied, setShowCopied] = useState(false);

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).format(date);
    };

    useEffect(() => {
        setLocalName(name);
    }, [name]);

    const handleNameChange = (value: string) => {
        if (value.length >= MAX_NAME_LENGTH && localName.length < MAX_NAME_LENGTH) {
            error({
                icon: AlertCircle,
                title: "Character Limit Reached",
                description: `Assistant names are limited to ${MAX_NAME_LENGTH} characters`,
                duration: 3000
            });
        }
        
        setLocalName(value);
        setName(value);
        
        if (value.trim() && value.length <= MAX_NAME_LENGTH) {
            setNameError('');
        } else if (value.length > MAX_NAME_LENGTH) {
            setNameError(`Name must be ${MAX_NAME_LENGTH} characters or less`);
        }
    };

    return (
        <Card className="shadow-sm">
            <CardHeader className="border-b bg-muted/20">
                <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                        <Info className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1 space-y-1">
                        <CardTitle>Assistant Information</CardTitle>
                        <CardDescription>Basic details and identification</CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Name Field */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Label htmlFor="name" className="flex items-center gap-2">
                                <Type className="h-4 w-4 text-primary" />
                                Name
                                <span className="text-destructive">*</span>
                            </Label>
                            <span className="text-xs text-muted-foreground">
                                {localName.length}/{MAX_NAME_LENGTH}
                            </span>
                        </div>
                        <Input
                            id="name"
                            value={localName}
                            onChange={(e) => handleNameChange(e.target.value)}
                            placeholder="Enter assistant name"
                            className={cn(nameError ? 'border-red-500' : '', 'h-9')}
                            maxLength={MAX_NAME_LENGTH}
                            required
                        />
                        {nameError ? (
                            <p className="text-sm text-red-500">{nameError}</p>
                        ) : (
                            <p className="text-xs text-muted-foreground">Maximum {MAX_NAME_LENGTH} characters</p>
                        )}
                    </div>

                    {/* ID Field - Only show in edit mode */}
                    {assistantId && (
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Hash className="h-4 w-4 text-primary" />
                                Assistant ID
                            </Label>
                            <div className="flex items-center gap-2">
                                <Input
                                    value={assistantId}
                                    readOnly
                                    className="h-9 font-mono text-sm flex-1 bg-muted/50"
                                />
                                <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={async () => {
                                                    try {
                                                        await navigator.clipboard.writeText(assistantId);
                                                        success({
                                                            icon: ClipboardCheck,
                                                            title: "ID Copied",
                                                            description: "Assistant ID copied to clipboard",
                                                            duration: 2000
                                                        });
                                                        setShowCopied(true);
                                                        setTimeout(() => setShowCopied(false), 2000);
                                                    } catch {
                                                        error({
                                                            icon: AlertCircle,
                                                            title: "Copy Failed",
                                                            description: "Failed to copy ID to clipboard",
                                                            duration: 3000
                                                        });
                                                    }
                                                }}
                                                className="shrink-0"
                                            >
                                                {showCopied ? (
                                                    <Check className="h-4 w-4 text-green-500" />
                                                ) : (
                                                    <Copy className="h-4 w-4" />
                                                )}
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent side="top">
                                            <p>{showCopied ? 'Copied!' : 'Copy ID'}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                            <p className="text-xs text-muted-foreground">Unique identifier for this assistant</p>
                        </div>
                    )}
                </div>

                {/* Timestamp Fields - Only show in edit mode */}
                {assistantId && (createdAt || updatedAt) && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Created At */}
                        {createdAt && (
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <Calendar className="h-4 w-4 text-primary" />
                                    Created
                                </Label>
                                <Input
                                    value={formatDate(createdAt)}
                                    readOnly
                                    className="h-9 bg-muted/50"
                                />
                                <p className="text-xs text-muted-foreground">When this assistant was created</p>
                            </div>
                        )}

                        {/* Updated At */}
                        {updatedAt && (
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-primary" />
                                    Last Updated
                                </Label>
                                <Input
                                    value={formatDate(updatedAt)}
                                    readOnly
                                    className="h-9 bg-muted/50"
                                />
                                <p className="text-xs text-muted-foreground">Last time this assistant was modified</p>
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

